import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../lib/supabase';
import { Platform, Share } from 'react-native';
// `Alert` daqui, nao do react-native: no navegador o Alert do RN e um stub
// vazio e engole tudo em silencio (ver src/utils/appAlert.js). Em nativo e
// reexportacao direta - comportamento do app inalterado.
import { Alert } from '../utils/appAlert';
import { getCachedUser } from './userCacheService';
import { getBlockedUserIds } from './moderationService';
import {
    compressImage,
    validateImageAsset,
    validateVideoAsset,
    extensionFromMime,
} from '../utils/community/media';
import { withRetry, shortId } from '../utils/community/retry';
import { captureException } from './telemetry';

/**
 * Serviço de Comunidade - Integração com Supabase
 * Gerencia posts, comentários, votos e posts salvos
 */

// ==================== POSTS ====================

// ⚠️ SEM CHAMADOR VIVO desde 2026-08-06 (o feed passou a filtrar por ÁREA).
// Mantido junto com TOPIC_CONFIG: é o de-para necessário caso um dia se queira
// preencher `area` dos posts antigos a partir do `topic`. Ver pendências (E5).
const getTopicFilterName = (topicId) => {
    if (!topicId || topicId === 'all') return null;
    return TOPIC_CONFIG[topicId]?.name || null;
};

/** `all` (ou vazio) = sem filtro. Caso contrário, o id da área. */
const getAreaFilter = (areaId) => (!areaId || areaId === 'all' ? null : areaId);

const normalizeSearchTerm = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();

const PROFILE_SOCIAL_SELECT = 'id, nome, username, is_verified, is_premium, nivel, xp_total, foto_url, avatar_seed';
const PROFILE_SOCIAL_SCHEMA_LIMITATION_CODE = 'PROFILE_SOCIAL_SCHEMA_LIMITATION';
const PROFILE_SOCIAL_SCHEMA_LIMITATION_MESSAGE = `profiles precisa expor estes campos para identidade social de posts: ${PROFILE_SOCIAL_SELECT}.`;

const getErrorText = (error) => [
    error?.code,
    error?.message,
    error?.details,
    error?.hint,
].filter(Boolean).join(' ').toLowerCase();

const isProfileSocialSchemaError = (error) => {
    const errorText = getErrorText(error);
    if (!errorText) return false;

    return error?.code === '42703'
        || error?.code === 'PGRST204'
        || (
            errorText.includes('profiles')
            && errorText.includes('column')
            && PROFILE_SOCIAL_SELECT.split(', ').some(column => errorText.includes(column))
        );
};

const buildProfileSocialSchemaError = (error) => {
    const schemaError = new Error(PROFILE_SOCIAL_SCHEMA_LIMITATION_MESSAGE);
    schemaError.code = PROFILE_SOCIAL_SCHEMA_LIMITATION_CODE;
    schemaError.cause = error;
    return schemaError;
};

const assertProfileSocialQuerySucceeded = (error) => {
    if (!error) return;
    if (isProfileSocialSchemaError(error)) throw buildProfileSocialSchemaError(error);
    throw error;
};

const isProfileSocialSchemaLimitation = (error) => (
    error?.code === PROFILE_SOCIAL_SCHEMA_LIMITATION_CODE
);

const normalizeOptionalText = (value) => {
    if (typeof value !== 'string') return null;

    const normalized = value.trim();
    return normalized || null;
};

const normalizeOptionalNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const resolveProfileAvatar = (profile, fallback) => (
    profile?.foto_url
    || (profile?.avatar_seed ? `https://api.dicebear.com/7.x/fun-emoji/png?seed=${encodeURIComponent(profile.avatar_seed)}&size=80` : null)
    || fallback
    || '👤'
);

const mapPostDto = (post, profile = null) => {
    const authorDisplayName = normalizeOptionalText(profile?.nome)
        || normalizeOptionalText(post.author_name)
        || 'Anônimo';

    return {
        id: post.id,
        author: normalizeOptionalText(post.author_name) || authorDisplayName,
        authorAvatar: resolveProfileAvatar(profile, post.author_avatar),
        authorDisplayName,
        authorUsername: normalizeOptionalText(profile?.username),
        authorIsVerified: profile?.is_verified === true,
        authorIsPremium: profile?.is_premium === true,
        authorLevel: normalizeOptionalNumber(profile?.nivel),
        authorXp: normalizeOptionalNumber(profile?.xp_total),
        topic: post.topic,
        area: post.area || null,
        topicIcon: post.topic_icon || '📝',
        topicColor: post.topic_color || '#5B8FF9',
        title: post.title,
        body: post.body,
        votes: post.votes || 0,
        comments: post.comments_count || 0,
        time: formatTimeAgo(post.created_at),
        tags: post.tags || [],
        postType: post.post_type || 'text',
        hasImage: !!post.image_url,
        imageUrl: post.image_url,
        videoUrl: post.video_url,
        solved: post.is_solved || false,
        userId: post.user_id,
    };
};

/**
 * Constrói um post OTIMISTA com o MESMO shape do feed real — reusa `mapPostDto`
 * (fonte única de shape) a partir do formulário de criação + o profile do próprio
 * autor (AuthContext). Usado pela CreatePostScreen para injetar o post no topo do
 * feed antes da resposta do servidor. `__isOptimistic` sinaliza à tela para
 * esmaecer/desabilitar o card até a reconciliação (refetch real).
 */
export const buildOptimisticPost = (form, profile, userId) => {
    const fakeRow = {
        id: `__optimistic_${Date.now()}`,
        user_id: userId,
        author_name: profile?.nome,
        author_avatar: profile?.foto_url,
        topic: form.topic,
        area: form.area || null,
        topic_icon: form.topicIcon,
        topic_color: form.topicColor,
        title: form.title,
        body: form.body,
        votes: 0,
        comments_count: 0,
        created_at: new Date().toISOString(),
        tags: form.tags || [],
        post_type: form.postType || 'text',
        image_url: form.imageUrl || null,
        video_url: form.videoUrl || null,
        is_solved: false,
    };
    return { ...mapPostDto(fakeRow, profile), __isOptimistic: true };
};

/**
 * Buscar posts com paginação KEYSET (cursor-based).
 * @param {Object} filters - Filtros opcionais
 * @param {string} filters.topic - Filtrar por tópico
 * @param {string} filters.search - Buscar por título/corpo
 * @param {string} filters.authorId - Filtrar por autor
 * @param {number} filters.limit - Limite de posts por página (padrão 10)
 * @param {{createdAt:string,id:string}} filters.cursor - Cursor da página anterior (null = 1ª página)
 * @returns {Promise<{posts:Array, hasMore:boolean, nextCursor:?{createdAt:string,id:string}}>}
 */
const getPostsLegacy = async (filters = {}) => {
    try {
        // Ordem determinística p/ keyset: created_at desc + id desc (desempate estável).
        let query = supabase
            .from('community_posts')
            .select('id, user_id, author_name, author_avatar, topic, topic_icon, topic_color, area, title, body, votes, comments_count, created_at, tags, image_url, video_url, post_type, is_solved')
            .order('created_at', { ascending: false })
            .order('id', { ascending: false });

        // Oculta posts derrubados pela moderação (auto-takedown ≥5 denúncias → status 'removed').
        query = query.neq('status', 'removed');

        // Oculta autores bloqueados pelo usuário atual (Trust & Safety). O banco filtra
        // (conteúdo bloqueado nem volta pro device). Set vazio quando logado-out.
        const blockedIds = await getBlockedUserIds();
        if (blockedIds.size > 0) {
            query = query.not('user_id', 'in', `(${[...blockedIds].join(',')})`);
        }

        // Filtro por autor (perfil "Posts" do usuário)
        if (filters.authorId) {
            query = query.eq('user_id', filters.authorId);
        }

        // Filtro por ÁREA (id estável). O antigo `ilike('topic', nome)` comparava
        // por RÓTULO e saiu de uso junto com os chips antigos.
        const areaFilter = getAreaFilter(filters.area);
        if (areaFilter) {
            query = query.eq('area', areaFilter);
        }

        // Filtro por busca: full-text INDEXADO (coluna fts + índice GIN). Substitui o
        // ilike '%termo%' (full scan, não escala). websearch_to_tsquery é tolerante à
        // sintaxe do usuário (aspas, OR, -negação) e nunca lança erro; word-aware (pt-BR).
        const searchTerm = normalizeSearchTerm(filters.search);
        if (searchTerm) {
            query = query.textSearch('fts', searchTerm, { type: 'websearch', config: 'portuguese' });
        }

        // Paginação KEYSET: pega posts "antes" do cursor (created_at,id) da página anterior.
        // Não duplica/pula quando há inserts entre páginas, nem degrada em páginas profundas
        // (usa o índice idx_community_posts_created_at). Aspas no timestamp (chars reservados).
        const cursor = filters.cursor;
        if (cursor?.createdAt && cursor?.id) {
            query = query.or(
                `created_at.lt."${cursor.createdAt}",and(created_at.eq."${cursor.createdAt}",id.lt.${cursor.id})`
            );
        }

        // limit+1 para detectar se há próxima página.
        const limit = filters.limit || 10;
        query = query.limit(limit + 1);

        const { data, error } = await query;

        if (error) throw error;

        const rows = data || [];
        const hasMore = rows.length > limit;
        const postsData = hasMore ? rows.slice(0, limit) : rows;

        // Resolve identidade social atual dos autores em batch (2 queries no total)
        const userIds = [...new Set(postsData.map(p => p.user_id).filter(Boolean))];
        let profileMap = {};
        if (userIds.length > 0) {
            const { data: profilesData, error: profilesError } = await supabase
                .from('profiles')
                .select(PROFILE_SOCIAL_SELECT)
                .in('id', userIds);

            assertProfileSocialQuerySucceeded(profilesError);

            if (profilesData) {
                profilesData.forEach(p => { profileMap[p.id] = p; });
            }
        }

        // Formata os posts para o formato esperado pelo componente
        const posts = postsData.map(post => mapPostDto(post, profileMap[post.user_id]));

        // Cursor da próxima página = created_at + id do último post desta página.
        const lastRow = postsData[postsData.length - 1];
        const nextCursor = lastRow ? { createdAt: lastRow.created_at, id: lastRow.id } : null;

        return {
            posts,
            hasMore,
            nextCursor,
        };
    } catch (error) {
        if (isProfileSocialSchemaLimitation(error)) throw error;

        // Não engolir em silêncio: em produção o transform-remove-console apaga logs,
        // então sem isto a falha real (rede/RLS/schema) fica invisível.
        captureException(error, { tags: { area: 'getPosts' } });
        return { posts: [], hasMore: false, nextCursor: null };
    }
};

// Curto-circuito por sessão: se o RPC get_feed NÃO existe no banco (db push pendente),
// não pagamos um round-trip falho por página. Só desliga em "função não encontrada"
// (PGRST202/404) — erro transitório (rede/RLS) NÃO desliga (pode voltar a tentar).
let feedRpcAvailable = true;

const isFunctionMissingError = (error) => (
    error?.code === 'PGRST202'
    || error?.code === '404'
    || /(could not find|function).*get_feed/i.test(error?.message || '')
);

/**
 * Caminho RÁPIDO do feed: RPC get_feed (1 round-trip, junta autor server-side).
 * Replica os filtros do getPostsLegacy. Retorna o MESMO shape ({posts,hasMore,nextCursor}).
 */
const getFeedViaRpc = async (filters = {}) => {
    const areaFilter = getAreaFilter(filters.area);
    const searchTerm = normalizeSearchTerm(filters.search);
    const cursor = filters.cursor;
    const limit = filters.limit || 10;

    const { data, error } = await supabase.rpc('get_feed', {
        p_topic: null,
        p_area: areaFilter,
        p_author: filters.authorId || null,
        p_search: searchTerm || null,
        p_limit: limit,
        p_cursor_created: cursor?.createdAt || null,
        p_cursor_id: cursor?.id || null,
    });

    if (error) throw error;

    const payload = data || {};
    // author = objeto aninhado (null-safe: LEFT JOIN sem perfil vem com id null → trata como sem perfil).
    const posts = (payload.posts || []).map((post) =>
        mapPostDto(post, post.author && post.author.id ? post.author : null)
    );

    return {
        posts,
        hasMore: !!payload.hasMore,
        nextCursor: payload.nextCursor || null,
    };
};

/**
 * Buscar posts com paginação KEYSET. Tenta o RPC get_feed (1 round-trip) e, se ele
 * ainda não existir no banco (db push pendente), cai no caminho antigo
 * (getPostsLegacy: 2 queries). Na web, erros transitórios são propagados para a
 * query: repetir imediatamente o mesmo acesso em duas queries prolonga o loading.
 * O aplicativo nativo mantém seu fallback anterior.
 *
 * Performance: depois do 1º "função não encontrada", `feedRpcAvailable` desliga e
 * todas as próximas páginas vão DIRETO ao legacy (zero round-trip falho extra) —
 * pré-push fica igual ao comportamento antigo, sem penalidade por página.
 */
export const getPosts = async (filters = {}) => {
    if (!feedRpcAvailable) {
        return getPostsLegacy(filters);
    }
    try {
        return await getFeedViaRpc(filters);
    } catch (rpcError) {
        if (isFunctionMissingError(rpcError)) {
            feedRpcAvailable = false; // db push pendente → não tenta mais o RPC nesta sessão
            if (__DEV__) console.warn('[getPosts] get_feed ausente — usando fallback até aplicar a migration/reiniciar');
            return getPostsLegacy(filters);
        }
        if (Platform.OS !== 'web') return getPostsLegacy(filters);
        throw rpcError;
    }
};

/**
 * Buscar um post específico por ID
 */
export const getPostById = async (postId) => {
    try {
        // Otimizado: colunas específicas
        const { data, error } = await supabase
            .from('community_posts')
            .select('id, user_id, author_name, author_avatar, topic, topic_icon, topic_color, title, body, votes, comments_count, created_at, tags, image_url, video_url, post_type, is_solved')
            .eq('id', postId)
            .single();

        if (error) throw error;

        // Resolve identidade social atual do autor
        let profile = null;
        if (data.user_id) {
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select(PROFILE_SOCIAL_SELECT)
                .eq('id', data.user_id)
                .maybeSingle();

            assertProfileSocialQuerySucceeded(profileError);

            profile = profileData;
        }

        return mapPostDto(data, profile);
    } catch (error) {
        if (isProfileSocialSchemaLimitation(error)) throw error;

        return null;
    }
};

/**
 * Criar um novo post
 */
export const createPost = async (postData) => {
    try {
        // Pegar usuário atual
        const user = await getCachedUser();

        if (!user) {
            return { success: false, error: 'Você precisa estar logado para criar um post.' };
        }

        // Buscar nome e avatar do perfil (foto real tem prioridade; senão DiceBear pelo seed)
        const { data: profile } = await supabase
            .from('profiles')
            .select('nome, foto_url, avatar_seed')
            .eq('id', user.id)
            .single();

        const resolvedAvatar = profile?.foto_url
            || `https://api.dicebear.com/7.x/fun-emoji/png?seed=${encodeURIComponent(profile?.avatar_seed || 'happy')}&size=80`;

        const { data, error } = await supabase
            .from('community_posts')
            .insert({
                user_id: user.id,
                author_name: profile?.nome || user.email?.split('@')[0] || 'Anônimo',
                author_avatar: resolvedAvatar,
                title: postData.title,
                body: postData.body,
                topic: postData.topic,
                area: postData.area || null,
                topic_icon: postData.topicIcon,
                topic_color: postData.topicColor,
                tags: postData.tags || [],
                post_type: postData.postType || 'text',
                image_url: postData.imageUrl || null,
                video_url: postData.videoUrl || null,
            })
            .select()
            .single();

        if (error) throw error;

        return { success: true, data };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

const POST_MEDIA_BUCKET = 'community-posts';

/**
 * Upload de mídia para o Supabase Storage via XHR — único caminho que expõe
 * progresso real (a SDK JS não emite eventos de progresso).
 * Inclui retry exponencial, AbortController e validação prévia.
 *
 * @param {Object}   params
 * @param {Object}   params.asset       - Asset retornado pelo expo-image-picker
 * @param {'image'|'video'} params.kind - Tipo de mídia
 * @param {AbortSignal=} params.signal
 * @param {(p:number)=>void=} params.onProgress - Progresso 0..1
 * @returns {Promise<{ publicUrl: string, path: string }>}
 */
export const uploadPostMedia = async ({ asset, kind, signal, onProgress }) => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('Você precisa estar logado para enviar arquivos.');

    // 1. Validação + compressão (apenas imagem)
    let finalUri = asset.uri;
    let mimeType = asset.mimeType || (kind === 'image' ? 'image/jpeg' : 'video/mp4');

    if (kind === 'image') {
        await validateImageAsset(asset);
        const compressed = await compressImage(asset.uri);
        finalUri = compressed.uri;
        mimeType = compressed.mimeType || mimeType;
    } else {
        await validateVideoAsset(asset);
    }

    const ext = extensionFromMime(mimeType);
    const fileName = `${shortId()}.${ext}`;
    const path = `${user.id}/${fileName}`;
    const uploadUrl = `${SUPABASE_URL}/storage/v1/object/${POST_MEDIA_BUCKET}/${path}`;
    const token = session.access_token;

    // 2. Upload via XHR com retry. Idempotência condicional: primeira tentativa
    //    rejeita overwrite; retries permitem (objeto pode ter sido parcialmente
    //    criado pela tentativa anterior).
    let attempt = 0;
    await withRetry(
        () => {
            const isRetry = attempt++ > 0;
            return uploadStorageFileViaXHR({
                uploadUrl, fileUri: finalUri, fileName, mimeType, token,
                signal, onProgress,
                upsert: isRetry,
            });
        },
        { max: 3, base: 800, signal },
    );

    const { data: { publicUrl } } = supabase.storage
        .from(POST_MEDIA_BUCKET)
        .getPublicUrl(path);

    return { publicUrl, path };
};

// FormData com { uri, type, name }: RN serializa como multipart streaming do
// disco, sem carregar o arquivo na memória. Único caminho confiável em RN para
// uploads grandes (vídeo até 50MB). Supabase Storage REST aceita multipart com
// campo de nome vazio — convenção da própria SDK.
export const uploadStorageFileViaXHR = ({ uploadUrl, fileUri, fileName, mimeType, token, signal, onProgress, upsert }) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let settled = false;

        const cleanup = () => {
            signal?.removeEventListener('abort', onAbort);
        };

        const onAbort = () => {
            if (settled) return;
            settled = true;
            try { xhr.abort(); } catch {}
            cleanup();
            const err = new Error('Aborted');
            err.name = 'AbortError';
            reject(err);
        };

        if (signal) {
            if (signal.aborted) return onAbort();
            signal.addEventListener('abort', onAbort);
        }

        xhr.open('POST', uploadUrl);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('apikey', SUPABASE_ANON_KEY);
        xhr.setRequestHeader('Cache-Control', '86400');
        xhr.setRequestHeader('x-upsert', upsert ? 'true' : 'false');
        // Content-Type NÃO é setado: RN gera multipart/form-data; boundary=...

        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
        };
        xhr.onload = () => {
            if (settled) return;
            settled = true;
            cleanup();
            if (xhr.status >= 200 && xhr.status < 300) {
                if (onProgress) onProgress(1);
                resolve();
            } else {
                reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText || 'upload falhou'}`));
            }
        };
        xhr.onerror = () => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error('Erro de rede durante upload.'));
        };
        xhr.ontimeout = () => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error('Timeout no upload.'));
        };

        // Timeout absoluto: 2min para vídeo de 50MB em conexão lenta
        xhr.timeout = 120000;

        // Convenção do Supabase Storage: campo de nome vazio.
        const formData = new FormData();
        formData.append('cacheControl', '86400');
        formData.append('', { uri: fileUri, type: mimeType, name: fileName });
        xhr.send(formData);
    });

/**
 * Deletar um post (apenas o autor pode)
 */
export const deletePost = async (postId) => {
    try {
        const { error } = await supabase
            .from('community_posts')
            .delete()
            .eq('id', postId);

        if (error) throw error;

        return { success: true };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

// ==================== VOTOS ====================

/**
 * Votar em um post (up ou down)
 */
export const votePost = async (postId, voteType) => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return { success: false, error: 'Você precisa estar logado para votar.' };
        }

        // Verificar se já votou
        const { data: existingVote } = await supabase
            .from('community_votes')
            .select('*')
            .eq('user_id', user.id)
            .eq('post_id', postId)
            .maybeSingle();

        if (existingVote) {
            if (existingVote.vote_type === voteType) {
                // Remove o voto se clicar no mesmo
                await supabase
                    .from('community_votes')
                    .delete()
                    .eq('id', existingVote.id);

                // Atualiza contador
                const delta = voteType === 'up' ? -1 : 1;
                await supabase.rpc('update_post_votes', { post_id: postId, delta });

                return { success: true, action: 'removed' };
            } else {
                // Muda o voto
                await supabase
                    .from('community_votes')
                    .update({ vote_type: voteType })
                    .eq('id', existingVote.id);

                // Atualiza contador (muda 2 pontos: remove antigo e adiciona novo)
                const delta = voteType === 'up' ? 2 : -2;
                await supabase.rpc('update_post_votes', { post_id: postId, delta });

                return { success: true, action: 'changed' };
            }
        } else {
            // Novo voto
            await supabase
                .from('community_votes')
                .insert({
                    user_id: user.id,
                    post_id: postId,
                    vote_type: voteType,
                });

            // Atualiza contador
            const delta = voteType === 'up' ? 1 : -1;
            await supabase.rpc('update_post_votes', { post_id: postId, delta });

            return { success: true, action: 'added' };
        }
    } catch (error) {

        return { success: false, error: error.message };
    }
};

/**
 * Verificar se o usuário já votou em um post
 */
export const getUserVote = async (postId) => {
    try {
        const user = await getCachedUser();

        if (!user) return null;

        const { data } = await supabase
            .from('community_votes')
            .select('vote_type')
            .eq('user_id', user.id)
            .eq('post_id', postId)
            .maybeSingle();

        return data?.vote_type || null;
    } catch (error) {
        return null;
    }
};

// ==================== LIKES ====================

/**
 * Toggle like em um post (curtir/descurtir)
 */
export const toggleLikePost = async (postId) => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return { success: false, error: 'Você precisa estar logado para curtir.', liked: false };
        }

        // RPC atômica: curte/descurte + atualiza o contador em 1 round-trip, race-safe
        // (UNIQUE user_id,post_id). Retorna { liked, votes } autoritativo do servidor.
        const { data, error } = await supabase.rpc('toggle_like', { target_post_id: postId });
        if (error) throw error;

        return { success: true, liked: data?.liked === true, votes: data?.votes };
    } catch (error) {

        return { success: false, error: error.message, liked: false };
    }
};

/**
 * Buscar IDs dos posts curtidos pelo usuário
 */
export const getUserLikes = async () => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return [];
        }

        const { data, error } = await supabase
            .from('community_post_likes')
            .select('post_id')
            .eq('user_id', user.id);

        if (error) throw error;

        return data.map(like => like.post_id);
    } catch (error) {

        return [];
    }
};

/**
 * Verificar se usuário curtiu um post específico
 */
export const isPostLiked = async (postId) => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return false;
        }

        const { data } = await supabase
            .from('community_post_likes')
            .select('id')
            .eq('post_id', postId)
            .eq('user_id', user.id)
            .maybeSingle();

        return !!data;
    } catch (error) {
        return false;
    }
};

// ==================== COMENTÁRIOS ====================

/**
 * Construir árvore hierárquica de comentários
 */
const buildCommentTree = (comments) => {
    const commentMap = new Map();
    const rootComments = [];

    // Criar mapa de todos os comentários
    comments.forEach(comment => {
        commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Construir hierarquia
    comments.forEach(comment => {
        const commentNode = commentMap.get(comment.id);
        if (comment.parentId) {
            const parent = commentMap.get(comment.parentId);
            if (parent) {
                parent.replies.push(commentNode);
            } else {
                rootComments.push(commentNode);
            }
        } else {
            rootComments.push(commentNode);
        }
    });

    return rootComments;
};

/**
 * Buscar comentários de um post (com hierarquia de threading)
 */
export const getComments = async (postId) => {
    try {
        // Otimizado: colunas específicas + limite
        const { data, error } = await supabase
            .from('community_comments')
            .select('id, parent_id, author_name, author_avatar, author_avatar_url, content, votes, created_at, user_id')
            .eq('post_id', postId)
            .order('created_at', { ascending: true })
            .limit(200);

        if (error) throw error;

        // Oculta comentários de autores bloqueados (Trust & Safety). buildCommentTree
        // reparenta órfãos como raiz, então respostas de não-bloqueados não somem.
        const blockedIds = await getBlockedUserIds();
        const visibleComments = blockedIds.size > 0
            ? data.filter(comment => !blockedIds.has(comment.user_id))
            : data;

        // Mapear dados para formato do app
        const mappedComments = visibleComments.map(comment => ({
            id: comment.id,
            parentId: comment.parent_id,
            author: comment.author_name,
            avatar: comment.author_avatar || '👤',
            avatarUrl: comment.author_avatar_url,
            content: comment.content,
            votes: comment.votes || 0,
            time: formatTimeAgo(comment.created_at),
            date: new Date(comment.created_at).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }),
            userId: comment.user_id,
        }));

        // Construir árvore hierárquica
        return buildCommentTree(mappedComments);
    } catch (error) {
        captureException(error, { tags: { area: 'getComments' } });
        return [];
    }
};

/**
 * Adicionar um comentário (com suporte a threading)
 */
export const addComment = async (postId, content, parentId = null) => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return { success: false, error: 'Você precisa estar logado para comentar.' };
        }

        // Buscar nome e foto do perfil
        const { data: profile } = await supabase
            .from('profiles')
            .select('nome, foto_url')
            .eq('id', user.id)
            .single();

        const { data, error } = await supabase
            .from('community_comments')
            .insert({
                post_id: postId,
                user_id: user.id,
                parent_id: parentId,
                author_name: profile?.nome || user.email?.split('@')[0] || 'Anônimo',
                author_avatar: '👤',
                author_avatar_url: profile?.foto_url || null,
                content: content.trim(),
            })
            .select()
            .single();

        if (error) throw error;

        // comments_count agora é mantido por trigger no banco (server-authoritative) —
        // não incrementamos manualmente aqui (evita dupla contagem).

        // Retornar comentário formatado
        return {
            success: true,
            data: {
                id: data.id,
                parentId: data.parent_id,
                author: data.author_name,
                avatar: data.author_avatar || '👤',
                avatarUrl: data.author_avatar_url,
                content: data.content,
                votes: 0,
                time: 'agora',
                date: new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }),
                userId: data.user_id,
                replies: [],
            }
        };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

/**
 * Deletar um comentário
 */
export const deleteComment = async (commentId, postId) => {
    try {
        const { error } = await supabase
            .from('community_comments')
            .delete()
            .eq('id', commentId);

        if (error) throw error;

        // comments_count mantido por trigger no banco — não decrementamos manualmente.

        return { success: true };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

// ==================== POSTS SALVOS ====================

/**
 * Salvar/Dessalvar um post
 */
export const toggleSavePost = async (postId) => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return { success: false, error: 'Você precisa estar logado para salvar posts.' };
        }

        // Verificar se já está salvo
        const { data: existing } = await supabase
            .from('community_saved_posts')
            .select('id')
            .eq('user_id', user.id)
            .eq('post_id', postId)
            .maybeSingle();

        if (existing) {
            // Remover dos salvos
            await supabase
                .from('community_saved_posts')
                .delete()
                .eq('id', existing.id);

            return { success: true, saved: false };
        } else {
            // Adicionar aos salvos
            await supabase
                .from('community_saved_posts')
                .insert({
                    user_id: user.id,
                    post_id: postId,
                });

            return { success: true, saved: true };
        }
    } catch (error) {

        return { success: false, error: error.message };
    }
};

/**
 * Verificar se um post está salvo
 */
export const isPostSaved = async (postId) => {
    try {
        const user = await getCachedUser();

        if (!user) return false;

        const { data } = await supabase
            .from('community_saved_posts')
            .select('id')
            .eq('user_id', user.id)
            .eq('post_id', postId)
            .maybeSingle();

        return !!data;
    } catch (error) {
        return false;
    }
};

/**
 * Buscar posts salvos do usuário
 */
export const getSavedPosts = async () => {
    try {
        const user = await getCachedUser();

        if (!user) return [];

        // Otimizado: colunas específicas no JOIN + limite
        const { data, error } = await supabase
            .from('community_saved_posts')
            .select(`
                post_id,
                community_posts (id, author_name, author_avatar, topic, topic_icon, topic_color, title, body, votes, comments_count, created_at, tags, image_url, is_solved)
            `)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(50);

        if (error) throw error;

        return data.map(item => ({
            id: item.community_posts.id,
            author: item.community_posts.author_name,
            authorAvatar: item.community_posts.author_avatar || '👤',
            topic: item.community_posts.topic,
            topicIcon: item.community_posts.topic_icon || '📝',
            topicColor: item.community_posts.topic_color || '#5B8FF9',
            title: item.community_posts.title,
            body: item.community_posts.body,
            votes: item.community_posts.votes || 0,
            comments: item.community_posts.comments_count || 0,
            time: formatTimeAgo(item.community_posts.created_at),
            tags: item.community_posts.tags || [],
            hasImage: !!item.community_posts.image_url,
            solved: item.community_posts.is_solved || false,
        }));
    } catch (error) {

        return [];
    }
};

/**
 * Buscar IDs dos posts salvos pelo usuário (leve — espelha getUserLikes).
 * Usado pelo feed para marcar o estado do botão salvar em cada card.
 */
export const getUserSavedPostIds = async () => {
    try {
        const user = await getCachedUser();

        if (!user) {
            return [];
        }

        const { data, error } = await supabase
            .from('community_saved_posts')
            .select('post_id')
            .eq('user_id', user.id);

        if (error) throw error;

        return data.map(item => item.post_id);
    } catch (error) {

        return [];
    }
};

// ==================== DENÚNCIAS (REPORT) ====================

/**
 * Denunciar um post. UNIQUE(post_id, reporter_id) impede denúncia dupla.
 * 5+ denúncias distintas disparam auto-takedown (trigger no banco → status 'removed').
 */
export const reportPost = async (postId, reason) => {
    try {
        const user = await getCachedUser();
        if (!user) {
            return { success: false, error: 'Você precisa estar logado para denunciar.' };
        }

        const { error } = await supabase
            .from('community_post_reports')
            .insert({ post_id: postId, reporter_id: user.id, reason });

        if (error) {
            if (error.code === '23505') {
                return { success: true, already: true }; // já denunciou (unique_violation)
            }
            throw error;
        }

        return { success: true };
    } catch (error) {

        return { success: false, error: error.message };
    }
};

// ==================== COMPARTILHAR ====================

/**
 * Compartilhar um post
 */
export const sharePost = async (post) => {
    try {
        await Share.share({
            title: post.title,
            message: `📚 ${post.title}\n\n${post.body}\n\n🏷️ ${post.tags?.join(' ') || ''}\n\n📱 Compartilhado via UniQuest`,
        });
        return { success: true };
    } catch (error) {

        return { success: false };
    }
};

// ==================== UTILITÁRIOS ====================

/**
 * Formata data para "há X tempo"
 */
const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'agora';
    if (diffMins < 60) return `há ${diffMins} min`;
    if (diffHours < 24) return `há ${diffHours} hora${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `há ${diffDays} dia${diffDays > 1 ? 's' : ''}`;
    if (diffDays < 30) return `há ${Math.floor(diffDays / 7)} semana${diffDays >= 14 ? 's' : ''}`;
    return `há ${Math.floor(diffDays / 30)} mês${diffDays >= 60 ? 'es' : ''}`;
};

/**
 * Mapeamento de tópicos com cores e ícones
 */
export const TOPIC_CONFIG = {
    concursos: { icon: '📋', color: '#5B8FF9', name: 'Concursos' },
    tecnologia: { icon: '💻', color: '#00D9FF', name: 'Tecnologia' },
    programacao: { icon: '👨‍💻', color: '#51CF66', name: 'Programação' },
    ia: { icon: '🤖', color: '#B341FF', name: 'IA' },
    entretenimento: { icon: '🎮', color: '#FF6B6B', name: 'Entretenimento' },
    ciencia: { icon: '🔬', color: '#FFD93D', name: 'Ciência' },
    carreira: { icon: '💼', color: '#FF1F7D', name: 'Carreira' },
    geral: { icon: '💬', color: '#FF6B6B', name: 'Geral' },
};

export default {
    getPosts,
    getPostById,
    createPost,
    deletePost,
    votePost,
    getUserVote,
    getComments,
    addComment,
    deleteComment,
    toggleSavePost,
    isPostSaved,
    getSavedPosts,
    getUserSavedPostIds,
    reportPost,
    sharePost,
    toggleLikePost,
    getUserLikes,
    isPostLiked,
    TOPIC_CONFIG,
};
